import { workspaceRoles, type WorkspaceRole } from "../types/roles.js";

export type WorkspaceEnvironment = "sandbox" | "production";

export type AccessCapabilityKey =
  | "view_audit"
  | "view_requests"
  | "view_policies"
  | "view_connectors"
  | "view_api_keys_meta"
  | "approve_sandbox"
  | "approve_production"
  | "mutate_members"
  | "mutate_workspace_security"
  | "mutate_policies_sandbox"
  | "mutate_policies_production"
  | "mutate_api_keys_sandbox"
  | "mutate_api_keys_production"
  | "mutate_connectors_sandbox"
  | "mutate_connectors_production"
  | "view_secret_values";

export const accessCapabilityKeys: AccessCapabilityKey[] = [
  "view_audit",
  "view_requests",
  "view_policies",
  "view_connectors",
  "view_api_keys_meta",
  "view_secret_values",
  "approve_sandbox",
  "approve_production",
  "mutate_members",
  "mutate_workspace_security",
  "mutate_policies_sandbox",
  "mutate_policies_production",
  "mutate_api_keys_sandbox",
  "mutate_api_keys_production",
  "mutate_connectors_sandbox",
  "mutate_connectors_production"
];

export type AccessManifest = {
  role: WorkspaceRole;
  strict_production_separation: boolean;
  can: Record<AccessCapabilityKey, boolean>;
};

export const buildAccessManifest = (input: {
  role: WorkspaceRole;
  strictProductionSeparation: boolean;
}): AccessManifest => {
  const { role, strictProductionSeparation } = input;
  const owner = role === "owner";
  const admin = role === "admin";
  const developer = role === "developer";
  const approver = role === "approver";

  const readCore =
    owner || admin || developer || approver || role === "viewer" || role === "auditor";
  const canApproveSandbox = owner || admin || approver || developer;
  const canApproveProductionStrict = owner || approver;
  const canApproveProduction = strictProductionSeparation
    ? canApproveProductionStrict
    : owner || admin || approver;

  return {
    role,
    strict_production_separation: strictProductionSeparation,
    can: {
      view_audit: readCore,
      view_requests: readCore,
      view_policies: readCore,
      view_connectors: readCore,
      view_api_keys_meta: owner || admin || developer,
      approve_sandbox: canApproveSandbox,
      approve_production: canApproveProduction,
      mutate_members: owner || admin,
      mutate_workspace_security: owner,
      mutate_policies_sandbox: owner || admin || developer,
      mutate_policies_production: owner || admin,
      mutate_api_keys_sandbox: owner || admin || developer,
      mutate_api_keys_production: owner || admin,
      mutate_connectors_sandbox: owner || admin || developer,
      mutate_connectors_production: owner || admin,
      view_secret_values: owner || admin || developer
    }
  };
};

export const buildRoleCapabilityMatrix = (input: {
  strictProductionSeparation: boolean;
}): AccessManifest[] =>
  workspaceRoles.map((role) =>
    buildAccessManifest({
      role,
      strictProductionSeparation: input.strictProductionSeparation
    })
  );

export const canMutatePoliciesInEnv = (input: {
  manifest: AccessManifest;
  environment: WorkspaceEnvironment;
}): boolean => {
  if (input.environment === "sandbox") {
    return input.manifest.can.mutate_policies_sandbox;
  }
  return input.manifest.can.mutate_policies_production;
};

export const canMutateApiKeysInEnv = (input: {
  manifest: AccessManifest;
  environment: WorkspaceEnvironment;
}): boolean => {
  if (input.environment === "sandbox") {
    return input.manifest.can.mutate_api_keys_sandbox;
  }
  return input.manifest.can.mutate_api_keys_production;
};

export const canMutateConnectorsInEnv = (input: {
  manifest: AccessManifest;
  environment: WorkspaceEnvironment;
}): boolean => {
  if (input.environment === "sandbox") {
    return input.manifest.can.mutate_connectors_sandbox;
  }
  return input.manifest.can.mutate_connectors_production;
};

export const canApproveInEnv = (input: {
  manifest: AccessManifest;
  environment: WorkspaceEnvironment;
}): boolean => {
  if (input.environment === "sandbox") {
    return input.manifest.can.approve_sandbox;
  }
  return input.manifest.can.approve_production;
};
